/* Native Shopify forms submit normally. No private app APIs or fixed product IDs. */
(() => {
  if (customElements.get('soule-kit-editor')) return;
  class SouleKit extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.observers = [];
      const on = (el, type, fn, options = {}) => el?.addEventListener(type, fn, { ...options, signal: this.abort.signal });
      const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
      const quantity = this.querySelector('[name="quantity"]');
      const form = this.querySelector('.soule-product-form');
      const storageKey = `soule-kit-quantity-${this.dataset.product}`;
      const normalizeQuantity = value => {
        const min = Number(quantity?.min) || 1;
        const step = Number(quantity?.step) || 1;
        const max = quantity?.max ? Number(quantity.max) : Infinity;
        const n = Math.max(min, Math.min(max, Number(value) || min));
        return min + Math.floor((n - min) / step) * step;
      };
      if (quantity) {
        try {
          const saved = sessionStorage.getItem(storageKey);
          if (saved !== null) {
            quantity.value = normalizeQuantity(saved);
            sessionStorage.removeItem(storageKey);
          }
        } catch (_) { /* Storage can be disabled without breaking purchase. */ }
        const updateQuantity = () => {
          this.querySelectorAll('[data-quantity-delta]').forEach(button => {
            const lower = Number(button.dataset.quantityDelta) < 0;
            button.disabled = lower ? Number(quantity.value) <= Number(quantity.min || 1) : Boolean(quantity.max) && Number(quantity.value) >= Number(quantity.max);
          });
        };
        this.querySelectorAll('[data-quantity-delta]').forEach(button => {
          on(button, 'click', () => {
            const delta = Number(button.dataset.quantityDelta) * (Number(quantity.step) || 1);
            quantity.value = normalizeQuantity(Number(quantity.value) + delta);
            quantity.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
        on(quantity, 'change', updateQuantity);
        updateQuantity();
      }
      on(this.querySelector('[data-variant-select]'), 'change', event => {
        try { sessionStorage.setItem(storageKey, quantity?.value || '1'); } catch (_) {}
        // Reload through Shopify so prices, inventory, payment buttons and apps share one variant.
        const url = new URL(location.href);
        url.searchParams.set('variant', event.target.value);
        url.hash = `soule-purchase-${this.dataset.section}`;
        location.assign(url.href);
      });
      this.querySelectorAll('[data-purchase-link]').forEach(link => on(link, 'click', event => {
        const destination = this.querySelector(`#soule-purchase-${CSS.escape(this.dataset.section)}`);
        if (!destination) return;
        event.preventDefault();
        destination.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
        destination.focus({ preventScroll: true });
      }));
      // Progressive enhancement: without JavaScript, every guide panel remains readable.
      const tabs = [...this.querySelectorAll('[data-guide-tab]')];
      const panels = [...this.querySelectorAll('[data-guide-panel]')];
      const selectTab = index => {
        tabs.forEach((tab, n) => {
          tab.setAttribute('aria-selected', String(n === index));
          tab.tabIndex = n === index ? 0 : -1;
        });
        panels.forEach(panel => { panel.hidden = panel.dataset.guidePanel !== tabs[index]?.dataset.guideTab; });
      };
      if (tabs.length) selectTab(Math.max(0, tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true')));
      tabs.forEach((tab, index) => {
        on(tab, 'click', () => selectTab(index));
        on(tab, 'keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); selectTab(next); tabs[next].focus(); }
        });
      });
      on(document, 'shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const panel = event.target.closest('[data-guide-panel]');
        if (panel) {
          const index = tabs.findIndex(tab => tab.dataset.guideTab === panel.dataset.guidePanel);
          if (index >= 0) selectTab(index);
          panel.scrollIntoView({ behavior: 'auto', block: 'center' });
        }
        const review = event.target.closest('.review-card');
        if (review) review.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
      });
      this.querySelectorAll('[data-carousel]').forEach(carousel => {
        const track = carousel.querySelector('[data-track]');
        const previous = carousel.querySelector('[data-prev]');
        const next = carousel.querySelector('[data-next]');
        const counter = carousel.querySelector('[data-counter]');
        const step = () => (track.firstElementChild?.getBoundingClientRect().width || 0) + (parseFloat(getComputedStyle(track).columnGap) || 0);
        const update = () => {
          const total = track.children.length;
          const width = step();
          const first = width ? Math.round(track.scrollLeft / width) : 0;
          const visible = Number(carousel.dataset.visible) || 1;
          previous.disabled = total === 0 || track.scrollLeft <= 2;
          next.disabled = total === 0 || track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
          counter.textContent = total ? `${first + 1}–${Math.min(first + visible, total)} de ${total}` : '';
          if (!total) carousel.hidden = true;
        };
        const move = direction => track.scrollBy({ left: direction * step() * (Number(carousel.dataset.step) || 1), behavior: reduced() ? 'auto' : 'smooth' });
        on(previous, 'click', () => move(-1)); on(next, 'click', () => move(1));
        on(track, 'scroll', update, { passive: true });
        on(track, 'keydown', event => {
          if (event.target !== track) return;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1); }
        });
        if (typeof ResizeObserver !== 'undefined') { const observer = new ResizeObserver(update); observer.observe(track); this.observers.push(observer); }
        update();
      });
      const players = [...this.querySelectorAll('video')];
      players.forEach(player => on(player, 'play', () => players.forEach(other => { if (other !== player) other.pause(); })));
      if (typeof IntersectionObserver !== 'undefined') {
        const observer = new IntersectionObserver(entries => entries.forEach(entry => { if (!entry.isIntersecting) entry.target.pause(); }), { threshold: 0.1 });
        players.forEach(player => observer.observe(player)); this.observers.push(observer);
      }
      const updateDates = () => {
        const timeline = this.querySelector('[data-delivery]');
        if (!timeline) return;
        const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
        const p = type => Number(parts.find(part => part.type === type).value);
        const base = Date.UTC(p('year'), p('month') - 1, p('day'), 12);
        const format = offset => new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(base + offset * 86400000)).replace(' de ', ' ');
        const transit = Number(timeline.dataset.transit) || 0;
        const min = Math.max(transit, Number(timeline.dataset.min) || 1);
        const max = Math.max(min, Number(timeline.dataset.max) || min);
        const dates = [format(0), format(transit), `${format(min)} – ${format(max)}`];
        timeline.querySelectorAll('[data-date]').forEach(node => { node.textContent = dates[Number(node.dataset.date)]; });
      };
      updateDates();
      on(document, 'visibilitychange', () => { if (document.hidden) players.forEach(player => player.pause()); else updateDates(); });
      // Native form validity and server validation remain authoritative.
      on(form, 'submit', event => { if (!form.checkValidity()) { event.preventDefault(); form.reportValidity(); } });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observers?.forEach(observer => observer.disconnect());
      this.querySelectorAll('video').forEach(player => player.pause());
    }
  }
  customElements.define('soule-kit-editor', SouleKit);
})();
